<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-white">
          {{ isEdit ? "Editar jugador histórico" : "Nuevo jugador histórico" }}
        </h1>
        <router-link
          to="/admin/pasado-futuro"
          class="btn-bochas-trans text-sm self-start sm:self-auto"
        >
          ← Volver
        </router-link>
      </div>

      <form @submit.prevent="onSave" class="card-fbochas p-4 sm:p-6 space-y-4">
        <!-- Foto -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Foto</label
          >
          <div class="flex items-center gap-4">
            <div
              class="flex-shrink-0 w-24 h-24 rounded-full overflow-hidden bg-bochas-bg border-2 border-gray-700"
            >
              <img
                v-if="photoUrl"
                :src="photoUrl"
                :alt="form.name || 'Foto'"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-gradient-to-br from-bochas-azul to-bochas-rojo flex items-center justify-center text-white text-xl font-bold"
              >
                {{ initials(form.name) }}
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label
                v-if="isEdit"
                class="btn-bochas-azul text-xs cursor-pointer inline-flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {{ photoUrl ? "Cambiar foto" : "Subir foto" }}
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onUploadPhoto"
                />
              </label>
              <p v-if="!isEdit" class="text-xs text-gray-500">
                Guardá el jugador histórico primero para subir la foto.
              </p>
              <span v-if="uploadingPhoto" class="text-xs text-gray-400"
                >Subiendo...</span
              >
            </div>
          </div>
          <p v-if="photoError" class="text-xs text-bochas-rojo mt-2">
            {{ photoError }}
          </p>
        </div>

        <!-- Datos -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Nombre *</label
          >
          <input
            v-model="form.name"
            type="text"
            required
            class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
            placeholder="Ej: Roberto Gómez"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Asociación o club</label
          >
          <SelectField
            v-model="form.association"
            :options="associationOptions"
            placeholder="Sin asociación o club"
          />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Fecha de nacimiento</label
            >
            <DatePickerField
              v-model="form.birthDate"
              name="birth-date"
              placeholder="Fecha de nacimiento"
              fixed-dropdown
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Fecha de fallecimiento
              <span class="text-xs text-gray-500 ml-1"
                >(dejar vacío si está vivo)</span
              >
            </label>
            <DatePickerField
              v-model="form.deathDate"
              name="death-date"
              placeholder="Fecha de fallecimiento"
              fixed-dropdown
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Inicio de carrera</label
            >
            <DatePickerField
              v-model="form.careerStartDate"
              name="career-start-date"
              placeholder="Fecha de inicio"
              fixed-dropdown
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Fecha de retiro</label
            >
            <DatePickerField
              v-model="form.careerEndDate"
              name="career-end-date"
              placeholder="En actividad"
              fixed-dropdown
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Bio / Descripción</label
          >
          <RichTextEditor
            v-model="form.bio"
            placeholder="Escribí la biografía del referente..."
            :min-height="280"
          />
        </div>

        <AchievementsField v-model="form.achievements" />

        <div class="rounded-xl border border-white/10 bg-bochas-bg/45 p-4 sm:p-5">
          <h2 class="mb-4 text-base font-semibold text-white">Contacto y redes</h2>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input v-model="form.phone" type="tel" class="admin-contact-input" placeholder="Teléfono" />
            <input v-model="form.whatsapp" type="tel" class="admin-contact-input" placeholder="WhatsApp con código de país" />
            <input v-model="form.email" type="email" class="admin-contact-input" placeholder="Email" />
            <input v-model="form.instagramUrl" type="url" class="admin-contact-input" placeholder="URL de Instagram" />
            <input v-model="form.facebookUrl" type="url" class="admin-contact-input sm:col-span-2" placeholder="URL de Facebook" />
          </div>
          <p class="mt-3 text-xs text-gray-500">Los campos vacíos no se mostrarán públicamente.</p>
        </div>

        <div class="flex items-center gap-4 sm:gap-6 flex-wrap">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.status"
              type="radio"
              value="draft"
              class="text-bochas-rojo focus:ring-bochas-rojo"
            />
            <span class="text-gray-300 text-sm">Borrador</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.status"
              type="radio"
              value="published"
              class="text-bochas-rojo focus:ring-bochas-rojo"
            />
            <span class="text-gray-300 text-sm">Publicado</span>
          </label>
        </div>

        <div
          v-if="error"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ error }}
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            :disabled="saving"
            class="btn-bochas-rojo"
            :class="{ 'opacity-60 cursor-not-allowed': saving }"
          >
            <span v-if="!saving">{{
              isEdit ? "Guardar cambios" : "Crear"
            }}</span>
            <span v-else>Guardando...</span>
          </button>
          <router-link
            to="/admin/pasado-futuro"
            class="btn-bochas-trans text-center"
          >
            Cancelar
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  crearReferente,
  editarReferente,
  obtenerReferente,
  subirFotoReferente,
} from "@/services/referentes/referentesService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { listarClubesAdmin } from "@/services/clubes/clubesService";
import DatePickerField from "@/components/Common/DatePickerField.vue";
import SelectField from "@/components/Common/SelectField.vue";
import { toDateInputValue } from "@/utils/date";
import AchievementsField from "@/components/Admin/AchievementsField.vue";
import RichTextEditor from "@/components/Common/RichTextEditor.vue";

const route = useRoute();
const router = useRouter();

const isEdit = computed(() => !!route.params.id);
const referenteId = computed(() => parseInt(route.params.id, 10));

const form = reactive({
  name: "",
  association: "",
  birthDate: "",
  deathDate: "",
  careerStartDate: "",
  careerEndDate: "",
  achievements: [""],
  phone: "",
  email: "",
  whatsapp: "",
  facebookUrl: "",
  instagramUrl: "",
  bio: "",
  status: "published",
});

const saving = ref(false);
const error = ref("");

const photoUrl = ref(null);
const uploadingPhoto = ref(false);
const photoError = ref("");
const asociaciones = ref([]);
const clubes = ref([]);

const selectedAssociationMissing = computed(
  () =>
    !!form.association &&
    !asociaciones.value.some((item) => item.name === form.association) &&
    !clubes.value.some((item) => item.name === form.association),
);

const associationOptions = computed(() => {
  const options = [{ value: "", label: "Sin asociación o club" }];
  if (selectedAssociationMissing.value) {
    options.push({ value: form.association, label: form.association });
  }
  return options.concat([
    ...asociaciones.value.map((asociacion) => ({
      value: asociacion.name,
      label: `Asociación · ${asociacion.name}`,
    })),
    ...clubes.value.map((club) => ({
      value: club.name,
      label: `Club · ${club.name}`,
    })),
  ]);
});

function initials(name) {
  if (!name) return "";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

async function loadReferente() {
  if (!isEdit.value) return;
  try {
    const data = await obtenerReferente(referenteId.value);
    Object.assign(form, {
      name: data.name || "",
      association: data.association || "",
      birthDate: toDateInputValue(data.birthDate),
      deathDate: toDateInputValue(data.deathDate),
      careerStartDate: toDateInputValue(data.careerStartDate),
      careerEndDate: toDateInputValue(data.careerEndDate),
      achievements: data.achievements?.length ? [...data.achievements] : [""],
      phone: data.phone || "",
      email: data.email || "",
      whatsapp: data.whatsapp || "",
      facebookUrl: data.facebookUrl || "",
      instagramUrl: data.instagramUrl || "",
      bio: data.bio || "",
      status: data.status || "published",
    });
    photoUrl.value = data.photo_url || null;
  } catch (err) {
    error.value = "No se pudo cargar el referente.";
  }
}

async function loadInstitutions() {
  try {
    const [associationsData, clubsData] = await Promise.all([
      listarAsociacionesAdmin(),
      listarClubesAdmin(),
    ]);
    asociaciones.value = associationsData.items || [];
    clubes.value = clubsData.items || [];
  } catch (_) {
    asociaciones.value = [];
    clubes.value = [];
  }
}

async function onSave() {
  if (
    form.careerStartDate &&
    form.careerEndDate &&
    form.careerEndDate < form.careerStartDate
  ) {
    error.value = "La fecha de retiro no puede ser anterior al inicio.";
    return;
  }
  saving.value = true;
  error.value = "";
  try {
    const payload = {
      name: form.name,
      association: form.association || null,
      birthDate: form.birthDate || null,
      deathDate: form.deathDate || null,
      careerStartDate: form.careerStartDate || null,
      careerEndDate: form.careerEndDate || null,
      achievements: form.achievements
        .map((item) => item.trim())
        .filter(Boolean),
      phone: form.phone || null,
      email: form.email || null,
      whatsapp: form.whatsapp || null,
      facebookUrl: form.facebookUrl || null,
      instagramUrl: form.instagramUrl || null,
      bio: form.bio || null,
      status: form.status,
    };
    if (isEdit.value) {
      await editarReferente(referenteId.value, payload);
    } else {
      const created = await crearReferente(payload);
      router.push(`/admin/pasado-futuro/referente/editar/${created.id}`);
      return;
    }
    router.push("/admin/pasado-futuro");
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo guardar.";
  } finally {
    saving.value = false;
  }
}

async function onUploadPhoto(e) {
  const file = e.target.files[0];
  if (!file) return;
  if (!file.type.startsWith("image/")) {
    photoError.value = "Solo se permiten imágenes.";
    return;
  }
  uploadingPhoto.value = true;
  photoError.value = "";
  try {
    const res = await subirFotoReferente(referenteId.value, file);
    photoUrl.value = res.url || res.referente?.photo_url;
    await loadReferente();
  } catch (err) {
    photoError.value = err.response?.data?.message || "Error al subir la foto.";
  } finally {
    uploadingPhoto.value = false;
    e.target.value = "";
  }
}

onMounted(async () => {
  await Promise.all([loadInstitutions(), loadReferente()]);
});
</script>

<style scoped>
.admin-contact-input {
  @apply w-full rounded-lg border border-gray-700 bg-bochas-bg px-4 py-3 text-white placeholder-gray-500 transition-colors focus:border-bochas-rojo focus:outline-none;
}
</style>
